import { useCallback, useEffect, useState } from "react";
import { ChevronDown, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { EmptyState, MonthFilter, PageHeader, StatCard } from "../components/Common";
import { DriverSelect } from "./admin/AdminPayments";
import { api, cleanParams, currentMonth, dateLabel, downloadFile, formatApiError, formatIDR, monthLabel } from "../lib/api";

function SplitSetting({ pct, onSaved }) {
  const [value, setValue] = useState(pct);
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(pct), [pct]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", { susuk_driver_pct: Number(value) }); toast.success("Pembagian susuk tersimpan"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rise rounded-2xl border border-slate-200 bg-white p-5 mb-6 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="susuk-split-card">
      <div className="flex-1">
        <p className="font-heading font-semibold text-slate-900">Pembagian Susuk</p>
        <p className="text-sm text-slate-500">Persentase bagian driver. Sisanya menjadi bagian perusahaan ({100 - (Number(value) || 0)}%).</p>
      </div>
      <div className="flex items-center gap-2">
        <Input type="number" min={0} max={100} className="w-24 font-num" value={value} onChange={(e) => setValue(e.target.value)} data-testid="susuk-pct-input" />
        <span className="text-sm text-slate-500">%</span>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="susuk-pct-save-btn">Simpan</Button>
      </div>
    </div>
  );
}

function DriverBlock({ r, pct, defaultOpen, i }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rise rounded-2xl border border-slate-200 bg-white overflow-hidden" style={{ animationDelay: `${i * 40}ms` }} data-testid={`susuk-driver-${r.driver_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full grid grid-cols-2 md:grid-cols-[1.4fr_0.6fr_1fr_1fr_1fr_24px] items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors" data-testid={`susuk-toggle-${r.driver_id}`}>
        <p className="col-span-2 md:col-span-1 font-semibold text-slate-900">{r.driver_name}</p>
        <p className="text-sm"><span className="text-slate-400">Trip </span><span className="font-num">{r.trips.length}</span></p>
        <p className="text-sm"><span className="md:hidden text-slate-400">Total </span><span className="font-num font-bold" data-testid={`susuk-total-${r.driver_id}`}>{formatIDR(r.total)}</span></p>
        <p className="text-sm"><span className="text-slate-400">Driver </span><span className="font-num text-emerald-700 font-semibold" data-testid={`susuk-driver-share-${r.driver_id}`}>{formatIDR(r.driver_share)}</span></p>
        <p className="text-sm"><span className="text-slate-400">Perusahaan </span><span className="font-num text-slate-700" data-testid={`susuk-company-share-${r.driver_id}`}>{formatIDR(r.company_share)}</span></p>
        <ChevronDown className={`hidden md:block h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-3 space-y-2">
          {r.trips.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`susuk-trip-${t.id}`}>
              <div className="min-w-0"><p className="text-xs text-slate-500">{dateLabel(t.trip_date)}</p><p className="truncate">{t.tour_name}</p></div>
              <div className="text-right shrink-0">
                <p className="font-num font-semibold">{formatIDR(t.susuk)}</p>
                <p className="text-[11px] text-slate-500 font-num">Driver {formatIDR(Math.round((t.susuk * pct) / 100))}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SusukPage({ isAdmin }) {
  const [month, setMonth] = useState(currentMonth());
  const [driverId, setDriverId] = useState("all");
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/susuk", { params: cleanParams({ month, driver_id: driverId }) }).then((r) => setData(r.data)), [month, driverId]);
  useEffect(() => { load(); }, [load]);
  const exportAs = (fmt) =>
    downloadFile("/susuk/export", { format: fmt, month, driver_id: driverId }, `rekap-susuk-${month || "semua"}.${fmt}`).catch((e) => toast.error(formatApiError(e)));

  return (
    <div>
      <PageHeader eyebrow="Rekap Susuk" title={monthLabel(month)}>
        <MonthFilter value={month} onChange={setMonth} />
        {isAdmin && <DriverSelect value={driverId} onChange={setDriverId} />}
        <Button variant="outline" className="h-10" onClick={() => exportAs("xlsx")} data-testid="susuk-export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        <Button variant="outline" className="h-10" onClick={() => exportAs("pdf")} data-testid="susuk-export-pdf-btn"><FileText className="h-4 w-4 mr-1" />PDF</Button>
      </PageHeader>
      {isAdmin && data && <SplitSetting pct={data.driver_pct} onSaved={load} />}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <div className="col-span-2"><StatCard tone="dark" label="Total Susuk" value={formatIDR(data.total)} hint={`Dicatat terpisah dari total bayar trip`} testId="susuk-stat-total" /></div>
            <StatCard label={`Bagian Driver (${data.driver_pct}%)`} value={formatIDR(data.driver_share)} testId="susuk-stat-driver" delay={80} />
            <StatCard label={`Bagian Perusahaan (${100 - data.driver_pct}%)`} value={formatIDR(data.company_share)} testId="susuk-stat-company" delay={160} />
          </div>
          {data.per_driver.length ? (
            <div className="space-y-3" data-testid="susuk-list">
              {data.per_driver.map((r, i) => <DriverBlock key={r.driver_id} r={r} pct={data.driver_pct} defaultOpen={!isAdmin} i={i} />)}
            </div>
          ) : (
            <EmptyState title="Belum ada susuk" text="Belum ada trip dengan susuk pada periode ini." testId="empty-susuk" />
          )}
        </>
      )}
    </div>
  );
}
